import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Clock, Coins, User, ArrowLeft, CheckCircle2, PartyPopper } from "lucide-react";
import { get, post } from "@/lib/api";
import { CATEGORY_LABELS, formatDate } from "@/lib/data";

export default function BookingConfirm() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [done, setDone] = useState(false);

  const { data: s } = useQuery({ queryKey: ["session", sessionId], queryFn: () => get(`/sessions/${sessionId}`) });
  const { data: me } = useQuery({ queryKey: ["me"], queryFn: () => get("/me") });

  const mutation = useMutation({
    mutationFn: () => post("/bookings", { session_id: sessionId }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["me"] });
      qc.invalidateQueries({ queryKey: ["bookings"] });
      setDone(true);
      toast.success("Rezervasyonun onaylandı!");
    },
    onError: (e) => toast.error(e?.response?.data?.detail || "Bir hata oluştu"),
  });

  if (!s || !me) return <div className="mx-auto max-w-2xl px-5 py-16 text-[#5C5856]">Yükleniyor…</div>;

  const newBalance = me.credits - s.credit_price;
  const insufficient = newBalance < 0;

  if (done) {
    return (
      <div className="mx-auto max-w-lg px-5 py-16 text-center">
        <div className="mx-auto flex h-20 w-20 animate-fade-up items-center justify-center rounded-full bg-[#4A5D4E]/10">
          <PartyPopper className="h-10 w-10 text-[#4A5D4E]" />
        </div>
        <h1 className="mt-6 animate-fade-up font-display text-4xl font-semibold">Rezervasyon onaylandı</h1>
        <p className="mt-2 text-[#5C5856]">{s.title} · {s.studio_name}</p>
        <div className="mt-6 rounded-2xl border border-[#E5E0D8] bg-white p-6 text-left">
          <div className="flex items-center justify-between py-1.5 text-sm"><span className="text-[#5C5856]">Tarih</span><span className="font-medium">{formatDate(s.date)} · {s.start_time}</span></div>
          <div className="flex items-center justify-between py-1.5 text-sm"><span className="text-[#5C5856]">Eğitmen</span><span className="font-medium">{s.instructor}</span></div>
          <div className="flex items-center justify-between border-t border-[#F0EAE0] pt-3 text-sm"><span className="text-[#5C5856]">Yeni kredi bakiyen</span><span className="flex items-center gap-1 font-semibold text-[#8C6A5D]"><Coins className="h-4 w-4" /> {newBalance}</span></div>
        </div>
        <div className="mt-6 flex gap-3">
          <Link to="/rezervasyonlarim" data-testid="view-my-bookings-button" className="flex-1 rounded-full bg-[#8C6A5D] py-3 font-medium text-white transition-colors hover:bg-[#705448]">Rezervasyonlarım</Link>
          <Link to="/kesfet" className="flex-1 rounded-full border border-[#E5E0D8] py-3 font-medium transition-colors hover:bg-[#F4EFE7]">Keşfetmeye devam et</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl px-5 py-10">
      <Link to={`/seans/${sessionId}`} className="mb-5 inline-flex items-center gap-1.5 text-sm text-[#5C5856] hover:text-[#2D2926]">
        <ArrowLeft className="h-4 w-4" /> Seansa dön
      </Link>
      <h1 className="font-display text-4xl font-semibold tracking-tight">Rezervasyonu onayla</h1>

      <div className="mt-8 overflow-hidden rounded-2xl border border-[#E5E0D8] bg-white">
        <div className="flex gap-4 border-b border-[#F0EAE0] p-5">
          <img src={s.studio?.cover} alt="" className="h-24 w-24 rounded-xl object-cover" />
          <div>
            <span className="text-xs font-medium uppercase tracking-wide text-[#8C6A5D]">{CATEGORY_LABELS[s.category]}</span>
            <h2 className="font-display text-2xl font-semibold leading-tight">{s.title}</h2>
            <p className="text-sm text-[#5C5856]">{s.studio_name}</p>
            <p className="mt-1 flex items-center gap-1.5 text-sm text-[#5C5856]"><Clock className="h-3.5 w-3.5" /> {formatDate(s.date)} · {s.start_time} <User className="ml-2 h-3.5 w-3.5" /> {s.instructor}</p>
          </div>
        </div>
        <div className="space-y-3 p-5">
          <div className="flex items-center justify-between text-sm"><span className="text-[#5C5856]">Seans ücreti</span><span className="flex items-center gap-1 font-medium"><Coins className="h-4 w-4 text-[#8C6A5D]" /> {s.credit_price} kredi</span></div>
          <div className="flex items-center justify-between text-sm"><span className="text-[#5C5856]">Mevcut bakiyen</span><span className="font-medium">{me.credits} kredi</span></div>
          <div className="flex items-center justify-between border-t border-[#F0EAE0] pt-3"><span className="font-medium">Rezervasyon sonrası bakiye</span><span className={`flex items-center gap-1 font-semibold ${insufficient ? "text-rose-600" : "text-[#8C6A5D]"}`}><Coins className="h-4 w-4" /> {newBalance}</span></div>
        </div>
      </div>

      {insufficient ? (
        <div className="mt-6 rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-700">
          Yetersiz kredi bakiyesi. <Link to="/kredi" className="font-medium underline">Kredi yükle</Link>
        </div>
      ) : (
        <button
          data-testid="confirm-booking-button"
          onClick={() => mutation.mutate()}
          disabled={mutation.isPending}
          className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#8C6A5D] py-4 font-medium text-white transition-colors hover:bg-[#705448] disabled:opacity-70"
        >
          <CheckCircle2 className="h-5 w-5" /> {mutation.isPending ? "İşleniyor…" : "Rezervasyonu onayla"}
        </button>
      )}
      <p className="mt-3 text-center text-xs text-[#9C948B]">Demo modu · Gerçek ödeme alınmaz</p>
    </div>
  );
}
